import React, { useState } from "react";
import * as api from "../../api";
import { inviteflowApiErrorMessage } from "../../utils/inviteflowErrors";

/**
 * Two-step OTP authentication for InviteFlow managers.
 * Step 1: Mobile (SMS) or email — OTP sent on the matching channel.
 * Step 2: Enter OTP → session opened (1 hour).
 */
const OTPAuthForm = ({ slug, onSuccess, onContinueAsGuest = () => {} }) => {
  const [step, setStep] = useState(1);
  const [contact, setContact] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const buildOtpPayload = () => {
    const t = contact.trim();
    if (!t) return null;
    if (t.includes("@")) {
      return { email: t.toLowerCase() };
    }
    return { phone: t };
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    const payload = buildOtpPayload();
    if (!payload) {
      setError("Enter your mobile number or email.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.inviteflow.requestOtp(slug, payload);
      setStep(2);
    } catch (err) {
      const detail =
        inviteflowApiErrorMessage(err) ||
        err?.response?.data?.detail ||
        "Failed to send OTP. Check your details.";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const payload = buildOtpPayload();
    if (!payload) {
      setError("Missing contact. Go back and enter mobile or email.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await api.inviteflow.authManager(slug, { ...payload, otp });
      onSuccess(res.data);
    } catch (err) {
      const detail =
        inviteflowApiErrorMessage(err) ||
        err?.response?.data?.detail ||
        (err?.response?.status === 401 ? "Invalid or expired OTP." : "Something went wrong. Please try again.");
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="inviteflow-auth">
      <div className="inviteflow-auth__card">
        <h2 className="inviteflow-auth__title">Manager Access</h2>
        <p className="inviteflow-auth__subtitle">
          {step === 1
            ? "Enter the mobile number or email on this invitation. We will send a one-time code by SMS or email."
            : `A 6-digit code was sent to ${contact.trim()}. Enter it below.`}
        </p>

        {error && <p className="inviteflow-auth__error">{error}</p>}

        {step === 1 ? (
          <form onSubmit={handleRequestOtp} className="inviteflow-auth__form">
            <label className="inviteflow-auth__label">
              Mobile number or email
              <input
                type="text"
                className="inviteflow-auth__input"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="+201001234567 or you@company.com"
                required
                autoFocus
                autoComplete="username"
              />
            </label>
            <button
              type="submit"
              className="inviteflow-btn inviteflow-btn--primary inviteflow-auth__btn"
              disabled={loading}
            >
              {loading ? "Sending…" : "Send Code"}
            </button>
            <button
              type="button"
              className="inviteflow-btn inviteflow-btn--secondary inviteflow-auth__btn"
              onClick={onContinueAsGuest}
            >
              Continue as Guest
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="inviteflow-auth__form">
            <label className="inviteflow-auth__label">
              Verification Code
              <input
                type="text"
                className="inviteflow-auth__input inviteflow-auth__input--otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="123456"
                maxLength={6}
                required
                autoFocus
                inputMode="numeric"
              />
            </label>
            <button
              type="submit"
              className="inviteflow-btn inviteflow-btn--primary inviteflow-auth__btn"
              disabled={loading}
            >
              {loading ? "Verifying…" : "Verify & Enter"}
            </button>
            <button
              type="button"
              className="inviteflow-btn inviteflow-btn--secondary inviteflow-auth__btn"
              onClick={() => { setStep(1); setOtp(""); setError(""); }}
            >
              Change contact
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default OTPAuthForm;
